import { z } from 'zod';
/**
 * A classification class as referenced by a destination's topic filter — the
 * minimal shape the Telegram destinations UI needs (full taxonomy lives in
 * `TopicDTO`). Used both for the per-destination selection (`topicFilter`) and
 * the catalog the UI offers (`availableTopics`).
 */
export const TelegramTopicRefSchema = z.object({
    id: z.guid(),
    key: z.string(),
    name: z.string(),
});
export const TelegramDestinationDtoSchema = z.object({
    id: z.string(),
    chatId: z.string(),
    messageThreadId: z.string().nullable(),
    kind: z.enum(['group', 'dm', 'channel']),
    title: z.string().nullable(),
    enabled: z.boolean(),
    // ACTIVE classes currently selected for this destination (archived/deleted
    // refs are dropped). Empty here does NOT necessarily mean "receives all" —
    // check `hasFilter` to disambiguate the archived-only case.
    topicFilter: z.array(TelegramTopicRefSchema),
    // Whether the destination has ANY filter rows at all (active OR archived).
    // `false` ⇒ truly unfiltered (receives every card). `true` while
    // `topicFilter` is empty ⇒ the filter exists but resolves to only
    // archived/deleted classes, so the destination matches NOTHING (fail-closed)
    // — the UI must surface this rather than reading it as «Все».
    hasFilter: z.boolean(),
});
/**
 * Response of `GET /api/telegram/destinations`: every destination (incl.
 * disabled/discovered) plus the full catalog of active classes the UI offers as
 * filter options.
 */
export const ListTelegramDestinationsResponseSchema = z.object({
    destinations: z.array(TelegramDestinationDtoSchema),
    availableTopics: z.array(TelegramTopicRefSchema),
});
export const UpdateTelegramDestinationRequestSchema = z
    .object({
    enabled: z.boolean().optional(),
    title: z.string().min(1).optional(),
    // Replace the destination's topic filter wholesale. Empty array clears it
    // (back to "receives every card"); omitted leaves the filter untouched.
    topicFilterIds: z.array(z.guid()).optional(),
})
    // An empty body is a no-op: reject it up front (400) so we never write an audit
    // row + run a DB round-trip for a request that changes nothing.
    .refine((b) => b.enabled !== undefined || b.title !== undefined || b.topicFilterIds !== undefined, {
    message: 'no fields to update',
});
//# sourceMappingURL=telegram-destination.js.map